// eslint-disable react/display-name
import React from "react";

import { Document, Image, Link, Page, Text, View } from "@react-pdf/renderer";

import { getReportPreviewEnd, getReportPreviewStart } from "utils/reportUtils";
import { extractSystemId, getGaugeTypeText } from "utils/utils";
import API_URLS from "constants/apis.url";
import { BarchartWidgetFormStrings, GenerateReport, HEADER_STRINGS, ReportTitleStrings } from "constants/constantStrings";
import { LOCAL_TZ_CODE } from "constants/globalConstants.js";
import { NORMALIZED_FILTERS, NormalizedFiltersStrings } from "constants/report.constant";
import { WIDGET_TYPES } from "constants/widgets.type.const";

import desalitechPic from "assets/PDF/desalitech.png";
import dupontLogo from "assets/PDF/DuPont_logo.png";
import infoIcon from "assets/PDF/info_icon.png";
import newheader from "assets/PDF/CCRO_new_header.png";
import waterSolution from "assets/PDF/water_solutions.png";

import ItemsTable from "./ItemsTable";
import styles from "./PDFDocument.style";

export const isToShowHeader = report =>
  report.Type === WIDGET_TYPES.TAG ||
  report.Type === WIDGET_TYPES.FREE_TEXT ||
  report.Type === WIDGET_TYPES.GAUGE ||
  (report.error && report.error !== "");

const reportDuration = () => `${getReportPreviewStart()} - ${getReportPreviewEnd()} ${LOCAL_TZ_CODE}`;
const getPDFText = (msg, style = "comment") => <Text style={styles[style]}>{msg}</Text>;

const getReportView = (report, index) => {
  const attributes = report.Attributes;
  const levelType = (attributes?.Function && BarchartWidgetFormStrings[attributes?.Function]) || attributes?.Function;
   
  if (report.Type === WIDGET_TYPES.TAG) {
    return (     
      <View style={styles.page} wrap={false}>
        {getPDFText(report.Header, "reportTitle")}
        {report.error && report.error !== "" ? (
          <View> {getPDFText(report.error, "error")} </View>
        ) : (
          <View>
            <View style={styles.tagMainContainer}> {getPDFText(report.tag, "tagText")} </View>
          </View>
        )}
        {getPDFText(` ${levelType}`,"subtitle")}
        {attributes?.Comments && attributes?.Comments !== "" && getPDFText(` ${attributes?.Comments}`)}
      </View>
    );
  } else {
    return (
      <View style={styles.page} key={index}>
        {isToShowHeader(report) && <Text style={styles.reportTitle}> {report.Header}</Text>}
        {report.url && <Image style={styles.widgetImage} src={report.url} />}
        {report.error && report.error !== "" && <Text style={styles.error}>{report.error}</Text>}
        {attributes?.Function && getPDFText(` ${levelType}`,"subtitle")}
        {report.SubType &&
          (report.Type === WIDGET_TYPES.GAUGE
           ? getPDFText(`${getGaugeTypeText(report.SubType)}`,"subtitle") 
          : getPDFText(`${report.SubType}`,"subtitle"))}
          
        {report.normalizeRecovery && getPDFText(`${NormalizedFiltersStrings[NORMALIZED_FILTERS.production]} ${report.normalizeRecovery}   ${NormalizedFiltersStrings[NORMALIZED_FILTERS.feedFlow]} ${report.normalizeFeedFlow}`, "subtitle")}
        {report.normalizeRejectConductivity &&getPDFText(`${NormalizedFiltersStrings[NORMALIZED_FILTERS.conductivity]} ${report.normalizeRejectConductivity}   ${ReportTitleStrings.baseline} ${report.normalizeBaseline}`,"subtitle")} 
       
        {report.predictionTime && getPDFText(`${report.predictionTime}`,"subtitle")} 
        {attributes?.Text && attributes?.Text !== "" && getPDFText(` ${attributes?.Text}`)}
        {attributes?.Comments && attributes?.Comments !== "" && getPDFText(`${attributes?.Comments}`)}

        {report.predictionNote && (
          <View style={styles.predictionNoteContainer}>
            <Image style={styles.infoIconImage} src={infoIcon} />
            {getPDFText(report.predictionNote, "predictionNote")}
          </View>
        )}      
      </View>
    );
  }
};

const PdfRender = React.memo(
  ({ reports, setReload, alarmUrl, alarms, systemName, title, reportDate }) => {
    const ITEMS_PER_PAGE = 1; // number of reports per page
    const reportPages = []; 
    reports.forEach((report, index) => {
      const currentPage = reportPages[reportPages.length - 1];
      if (!currentPage || currentPage.length >= ITEMS_PER_PAGE) {
        reportPages.push([report]); 
      } else {
        currentPage.push(report);
      }
    });
    return (
      <Document
        title={title}
        onLoadSuccess={(info) => {
          setReload(false);
        }}
      >
        <Page style={styles.body}>
          <View style={styles.flexRow}>
            <Image style={styles.waterSolutionImage} src={waterSolution} />
            <Image style={styles.dupontLogoImage} src={dupontLogo} />
          </View>
          <Image src={desalitechPic} />
          <View style={styles.titleContainer}>
            {getPDFText(HEADER_STRINGS.desaliTec, "desalitext")}
            {getPDFText(GenerateReport.TM, "mainPageTitleTopMiddle")}
            {getPDFText(HEADER_STRINGS.soarSmart, "desalitext")}
          </View>

          <Text style={styles.ccroTitle}>{title} </Text>
          

          <View style={styles.footer} fixed>
            <View>
              {getPDFText(`${GenerateReport.sysId}  ${extractSystemId()}`, "systemDetail")}
              {getPDFText(`${GenerateReport.sysName}  ${systemName}`, "systemDetail")}
              {getPDFText(`${GenerateReport.period}  ${reportDuration()}`, "systemDetail")}
            </View>
            <Link style={styles.link} src={API_URLS.dupontWaterSolution}>
              <Text> {GenerateReport.watersolutionLink}</Text>
            </Link>
          </View>
        </Page>
        {reportPages.map((pageReports, pageIndex) => (
          <Page key={`page-${pageIndex}`} style={styles.contentPage}>
            <View style={styles.header} fixed>
              <Image src={newheader} />
            </View>
            {pageReports.map((report, reportIndex) => getReportView(report, reportIndex))}
           
            <Text
              style={styles.pageNumber}
              render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
              fixed
            />
          </Page>

        ))}
        <Page  style={styles.contentPage}>
          <Image style={styles.widgetImage} src={alarmUrl} />
          {alarms && alarms.data.Alarms.length > 0 ? (
            <ItemsTable data={alarms.data.Alarms} />
          ) : (
            <Text style={styles.noAlarms}>
              {GenerateReport.noAlarms.replace("[%d]", reportDuration())}
            </Text>    
          )}
          <Text
              style={styles.pageNumber}
              render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
              fixed
            />
          </Page>
      </Document>
    );
  }
);

export default PdfRender;
